import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { InvoiceForm, InvoiceFormData } from '@lib/invoice-form';
import { UpdateInvoiceDateDialog, UpdateNextInvoiceDateData } from '@lib/update-invoice-date-dialog';

import { Addon, Agreement, Milestone, Product, Purchased } from '../schemas';
import { AgreementService, Auth, ClientService, ProductService } from '../service';
import { MatSort, MatSortModule } from '@angular/material/sort';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    CommonModule,
    RouterModule,

    MatTableModule,
    MatExpansionModule,
    MatIconModule,
    MatSortModule,
    MatButtonModule,
    MatToolbarModule,
    MatTooltipModule,
    DatePipe,
  ],
  templateUrl: './dashboard-page.html',
})
export class DashboardPage {
  pending = signal(false);

  dialog = inject(MatDialog);

  clientService = inject(ClientService);

  agreementService = inject(AgreementService);

  productService = inject(ProductService);

  route = inject(ActivatedRoute);

  authService = inject(Auth);

  clientNames = computed(() => this.clientService.names());
  milestoneStatuses = {
    0: 'Not ready to invoice',
    10: 'Ready to invoice',
    20: 'Invoiced',
  };

  expiringAgreementsSort = viewChild.required<MatSort>("expiringAgreementsSort")
  expiringAgreements = this.agreementService.expiring;
  expiringAgreementsSource = computed(() => {
    const source = new MatTableDataSource(this.expiringAgreements())
    source.sort = this.expiringAgreementsSort()
    return source
  })

  dueInvoices = computed(() => {
    const agreements = this.agreementService.agreements();
    const now = new Date();

    return agreements.filter((it) => {
      const invoiceDate = it.NextInvoiceDate ? new Date(it.NextInvoiceDate) : now;
      const diff = invoiceDate.getTime() - now.getTime();
      const invoiceIn = Math.ceil(diff / 1000 / 3600 / 24);
      it['_invoiceIn'] = invoiceIn;

      return it.Status !== 'Expired' && invoiceIn <= 14;
    });
  });

  dueInvoicesSort = viewChild.required<MatSort>("dueInvoicesSort")
  dueInvoicesSource = computed(() => {
    const source = new MatTableDataSource(this.dueInvoices())
    source.sort = this.dueInvoicesSort()
    return source
  })

  addonsSort = viewChild.required<MatSort>("addonsSort");
  addons = this.agreementService.separateAddons;
  addonsSource = computed(() => {
    const source = new MatTableDataSource(this.addons())
    source.sort = this.addonsSort()
    return source
  })

  milestonesSort = viewChild.required<MatSort>("milestonesSort");
  milestones = computed(() => {
    const products = this.productService.products();
    const result: (Milestone & { JobNumber: string })[] = [];
    products.forEach(({ JobNumber, Milestones }) =>
      Milestones.forEach((it) => it.Status === 10 && result.push({ ...it, JobNumber })),
    );
    return result;
  });
  milestonesSource = computed(() => {
    const source = new MatTableDataSource(this.milestones())
    source.sort = this.milestonesSort()
    return source
  })


  purchasedHoursSort = viewChild.required<MatSort>("purchasedHoursSort");
  purchasedHours = computed(() => {
    const items = [...this.productService.products(), ...this.agreementService.agreements()];

    const result: (Purchased & { _owner: { type: string; id: string } })[] = [];

    items.map((item) =>
      item.PurchasedHours.forEach(
        (it) =>
          it.Status === 10 &&
          result.push({
            ...it,
            _owner: {
              type: (item as Agreement).AgreementNumber ? 'agreement' : 'opportunity',
              id: (item as Agreement).AgreementNumber || (item as Product).JobNumber,
            },
          }),
      ),
    );

    return result;
  });
  purchasedHoursSource = computed(() => {
    const source = new MatTableDataSource(this.purchasedHours())
    source.sort = this.purchasedHoursSort()
    return source
  })


  trackByInvoice(idx: number, row: Agreement) {
    return row.AgreementNumber;
  }

  trackByAddon(idx: number, row: Addon) {
    return row.Id;
  }
  trackByMilestone(idx: number, row: Milestone) {
    return row.Id;
  }

  trackByPurchasedHours(idx: number, row: Purchased) {
    return row.Id;
  }

  trackByExpiring(idx: number, row: Agreement) {
    return row.AgreementNumber;
  }

  updateNextInvoiceDate(agreement: Agreement, addon?: Addon): void {
    const data: UpdateNextInvoiceDateData = { agreement, addon };
    this.dialog
      .open(UpdateInvoiceDateDialog, { data })
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result) {
            if (addon) {
              this.reloadAddons();
            } else {
              this.reloadDueInvoices();
            }
          }
        },
      });
  }

  reloadAddons(): void {
    this.agreementService.reload();
  }

  reloadDueInvoices(): void {
    this.agreementService.reload();
  }

  reloadMilestones() {
    this.productService.reload();
  }

  reloadPurchasedHours() {
    this.productService.reload();
    this.agreementService.reload();
  }
  invoicePurchasedHours(item: Purchased, ownerType: 'product' | 'agreement', ownerId: string) {
    const data: InvoiceFormData = {
      item,
      ownerType,
      ownerId,
      projects: [],
      hoursType: 'purchased',
    };
    this.dialog
      .open(InvoiceForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.reloadPurchasedHours(),
      });
  }

  invoiceMilestone(item: Milestone, JobNumber: string) {
    const data: InvoiceFormData = {
      item,
      ownerId: JobNumber,
      projects: [],
    };
    this.dialog
      .open(InvoiceForm, { data })
      .afterClosed()
      .subscribe({
        next: (result) => result && this.reloadMilestones(),
      });
  }
}
